export interface NameCapableObject {
  firstName?: string
  lastName?: string
  email?: string
  fullName?: string
  name?: string
}

export interface Options {
  emailFallback?: boolean
  fallbackString?: string
}

/**
 * Returns the most appropriate name representation from a given object.
 *
 * The function checks the provided `data` object for the following fields in order of priority:
 * - `fullName`
 * - `name`
 * - Both `firstName` and `lastName`
 * - `lastName`
 * - `firstName`
 * - `email` (if `emailFallback` is enabled)
 *
 * If none of these fields are present or non-empty, it returns an empty string.
 *
 * @param data - The object containing possible name fields.
 * @param options - Optional settings.
 * @param options.emailFallback - If `true` (default), falls back to using the email if no name fields are found.
 * @returns The best available name string, or an empty string if none found.
 */
export const getName = (data?: NameCapableObject | null | unknown, options: Options = {}): string => {
  const { emailFallback = true, fallbackString = '' } = options

  if (!data) return fallbackString

  if (typeof data !== 'object' || Array.isArray(data)) {
    return fallbackString
  }

  if ('fullName' in data && data.fullName && typeof data.fullName === 'string' && data.fullName.trim()) {
    return data.fullName.trim()
  }

  if ('name' in data && typeof data.name === 'string' && data.name.trim()) {
    return data.name.trim()
  }

  const sanitizedFirstName =
    'firstName' in data && typeof data.firstName === 'string' && data.firstName ? data.firstName.trim() : ''
  const sanitizedLastName =
    'lastName' in data && typeof data.lastName === 'string' && data.lastName ? data.lastName.trim() : ''

  if (sanitizedFirstName && sanitizedLastName) {
    return `${sanitizedFirstName} ${sanitizedLastName}`
  }

  if (sanitizedLastName) {
    return sanitizedLastName
  }

  if (sanitizedFirstName) {
    return sanitizedFirstName
  }

  if (emailFallback) {
    const sanitizedEmail = 'email' in data && data.email && typeof data.email === 'string' ? data.email.trim() : ''
    if (sanitizedEmail) {
      return sanitizedEmail
    }
  }

  return fallbackString
}
